import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { PromoBannerSlot, ZoomCard, richHtmlSx, useGetByDevice } from '@onesource/components';
import type { TContentBlockBase } from '@onesource/schemas';
import {
  ContentBlockType,
  EContentStatus,
  LessonStatus,
  LessonTemplateType,
  PromoBannerPosition,
} from '@onesource/schemas';
import DOMPurify from 'dompurify';
import { useState } from 'react';
import { MdFolder, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

import { DraftBadge } from '#/features/community-hub/components/draft-badge';
import { ProgressCard } from '#/features/community-hub/components/progress-card';
import { QuizCard } from '#/features/community-hub/components/quiz-card';
import { usePreviewMode } from '#/features/community-hub/hooks/use-preview-mode';

import { DocumentCard } from './document-card';
import { EmbedBlockContent } from './embed-block-content';
import { EmbedCard } from './embed-card';
import { FormCard } from './form-card';
import { GcsUploadCard } from './gcs-upload-card';
import { LessonContentSummary } from './lesson-content-summary';
import { PdfBlockViewer } from './pdf-block-viewer';
import { RedirectCard } from './redirect-card';
import { SubLessonsSection } from './sub-lessons-section';
import { TextBlockContent } from './text-block-content';
import { TextCard } from './text-card';
import type { LessonDetailViewProps } from './types';
import { VideoBlockPlayer } from './video-block-player';
import { VideoCard } from './video-card';

export function LessonDetailView({
  lesson,
  progressPercent,
  breadcrumbs,
  onNavigateToBlock,
  onNavigateToSubLesson,
}: LessonDetailViewProps) {
  const theme = useTheme();
  const { isPreview } = usePreviewMode();
  const { isMobile } = useGetByDevice();

  // The description is long on most lessons — on mobile it collapses behind a
  // Read More toggle so the video and the lesson sections stay reachable.
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const isDescriptionCollapsed = isMobile && !isDescriptionExpanded;

  const showProgressBar = lesson.showProgressBar !== false;

  // Inline template renders video & text directly on the lesson page (no
  // per-block navigation); other block types keep their navigating cards.
  const isInlineVideo = lesson.templateType === LessonTemplateType.INLINE;
  // Inline-document template does the same for PDF & text: the document is
  // embedded on the page like a video instead of opening a separate page.
  const isInlineDocument = lesson.templateType === LessonTemplateType.INLINE_DOCUMENT;
  // Both inline templates share the page-width layout.
  const isInlineLayout = isInlineVideo || isInlineDocument;

  const hasSubLessons = (lesson.subLessonCount || 0) > 0 || (lesson.children && lesson.children.length > 0);

  // Hide draft/archived blocks (and FORM blocks whose linked form isn't
  // published) from members; reviewers in preview mode keep seeing them
  // (badged) so they can review the full lesson before publish.
  const sortedBlocks = [...(lesson.contentBlocks || [])]
    .sort((a, b) => a.position - b.position)
    .filter(b => {
      if (isPreview) {
        return true;
      }
      if (b.status !== EContentStatus.PUBLISHED) {
        return false;
      }
      return b.blockType !== ContentBlockType.FORM || b.formStatus === 'published';
    });

  // In preview, wrap a block's card with a Draft badge when it's unpublished.
  const withDraftBadge = (block: TContentBlockBase, node: React.ReactNode) => {
    if (!isPreview || block.status === EContentStatus.PUBLISHED) {
      return node;
    }
    return (
      <Box key={block.id} sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}>
          <DraftBadge />
        </Box>
        {node}
      </Box>
    );
  };
  const videoBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.VIMEO);
  const documentBlocks = sortedBlocks.filter(
    b =>
      b.blockType === ContentBlockType.PDF ||
      b.blockType === ContentBlockType.TEXT ||
      b.blockType === ContentBlockType.REDIRECT ||
      b.blockType === ContentBlockType.EMBED ||
      b.blockType === ContentBlockType.FORM ||
      b.blockType === ContentBlockType.GCS_UPLOAD ||
      b.blockType === ContentBlockType.EVENT,
  );

  const quizBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.QUIZ);

  // Inline templates render their own block types inline in position order —
  // text for INLINE (its videos render above the fold), text/PDF/embed for
  // INLINE_DOCUMENT. Whatever is left of the document-type blocks still shows
  // in the grouped "Documents" section below.
  const inlineDocumentBlockTypes = [ContentBlockType.TEXT, ContentBlockType.PDF, ContentBlockType.EMBED];
  const inlineMediaBlocks = sortedBlocks.filter(b =>
    isInlineDocument ? inlineDocumentBlockTypes.includes(b.blockType) : b.blockType === ContentBlockType.TEXT,
  );

  // Inline template: videos are lifted above the description row so they land
  // above the fold; only text blocks stay in the inline stack below it.
  const heroVideoBlocks = isInlineVideo && !hasSubLessons ? videoBlocks : [];
  const inlineBlockTypes = isInlineDocument ? inlineDocumentBlockTypes : [ContentBlockType.TEXT];
  const documentBlocksForView = isInlineLayout
    ? documentBlocks.filter(b => !inlineBlockTypes.includes(b.blockType))
    : documentBlocks;

  const videoSectionHeading = lesson.videoSectionTitle?.trim() || 'Video/s';
  const documentSectionHeading = lesson.documentSectionTitle?.trim() || 'Documents';
  const quizSectionHeading = lesson.quizSectionTitle?.trim() || 'Quiz';

  // Inline template aligns every row to the top section's width (left card 631 +
  // 24 gap + summary 368 = 1023) so the video, documents and dividers all share
  // the same right edge. The default template keeps its original widths.
  const INLINE_ROW_MAX = 1023;
  const rowWidthSx = isInlineLayout
    ? { width: '100%', maxWidth: { md: INLINE_ROW_MAX } }
    : { width: { xs: '100%', md: 'calc(100% - 190px)' } };

  return (
    <Box>
      <Box>
        {breadcrumbs}

        {/* Inline template: the player sits directly under the breadcrumbs so
            the video is above the fold, ahead of the lesson sections. */}
        {heroVideoBlocks.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3, ...rowWidthSx }}>
            {heroVideoBlocks.map(block =>
              withDraftBadge(block, <VideoBlockPlayer key={block.id} block={block} lessonId={lesson.id} hero />),
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 631px' },
              maxWidth: { xs: '100%', md: 631 },
              bgcolor: 'white',
              borderRadius: '15px',
              p: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Typography variant="h5" sx={{ fontSize: '16px', fontWeight: 'bold', color: theme.palette.common.black }}>
                {lesson.title}
              </Typography>
              {isPreview && lesson.status !== LessonStatus.PUBLISHED && <DraftBadge />}
              {hasSubLessons && (
                <Chip
                  icon={<MdFolder size={16} />}
                  label={`${lesson.subLessonCount || lesson.children?.length || 0} sub-lessons`}
                  size="small"
                  color="secondary"
                  variant="filled"
                />
              )}
            </Box>

            <Typography
              variant="body2"
              component="div"
              sx={{
                ...richHtmlSx,
                color: theme.palette.common.black,
                lineHeight: 1.6,
                fontSize: '14px',
                whiteSpace: 'pre-line',
                ...(isDescriptionCollapsed && {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 5,
                  overflow: 'hidden',
                }),
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(lesson.longDescription || 'No description available'),
              }}
            />

            {isMobile && !!lesson.longDescription && (
              <Button
                variant="text"
                disableRipple
                onClick={() => setIsDescriptionExpanded(prev => !prev)}
                endIcon={isDescriptionExpanded ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                sx={{ px: 0, mt: 1, fontSize: '14px', fontWeight: 'bold', '&:hover': { background: 'none' } }}
              >
                {isDescriptionExpanded ? 'Read Less' : 'Read More'}
              </Button>
            )}
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '0 0 368px' },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <LessonContentSummary
              lesson={lesson}
              hasSubLessons={!!hasSubLessons}
              videoBlocks={videoBlocks}
              documentBlocks={documentBlocks}
              quizBlocks={quizBlocks}
              onNavigateToBlock={onNavigateToBlock}
            />

            {showProgressBar && <ProgressCard progressPercent={progressPercent} />}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 4, ...rowWidthSx }} />

      {hasSubLessons && <SubLessonsSection parentId={lesson.id} onNavigateToSubLesson={onNavigateToSubLesson} />}

      {hasSubLessons && <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />}

      {/* Inline template: render video & text on the page in position order. Other
          block types fall through to the grouped Documents/Quiz sections below. */}
      {!hasSubLessons && isInlineLayout && inlineMediaBlocks.length > 0 && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...rowWidthSx }}>
            {inlineMediaBlocks.map(block => {
              let card: React.ReactNode;
              if (block.blockType === ContentBlockType.VIMEO) {
                card = <VideoBlockPlayer key={block.id} block={block} lessonId={lesson.id} compact />;
              } else if (block.blockType === ContentBlockType.PDF) {
                card = <PdfBlockViewer key={block.id} block={block} />;
              } else if (block.blockType === ContentBlockType.EMBED) {
                card = <EmbedBlockContent key={block.id} block={block} />;
              } else {
                card = <TextBlockContent key={block.id} block={block} lessonId={lesson.id} fullWidth />;
              }
              return withDraftBadge(block, card);
            })}
          </Box>
          {(documentBlocksForView.length > 0 || quizBlocks.length > 0) && <Divider sx={{ my: 4, ...rowWidthSx }} />}
        </>
      )}

      {!hasSubLessons && !isInlineVideo && videoBlocks.length > 0 && (
        <>
          <Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
            >
              {videoSectionHeading}
            </Typography>
            <Box sx={{ py: 1, display: 'flex', gap: 3, overflowX: 'auto' }}>
              {videoBlocks.map(block =>
                withDraftBadge(
                  block,
                  <VideoCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />,
                ),
              )}
            </Box>
          </Box>
          <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />
        </>
      )}

      {!hasSubLessons && documentBlocksForView.length > 0 && (
        <>
          <Box
            pt={3}
            pb={4}
            sx={{
              backgroundColor: theme.palette.common.white,
              ...rowWidthSx,
              borderRadius: '15px',
              p: 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
            >
              {documentSectionHeading}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
              {documentBlocksForView.map(block => {
                let card: React.ReactNode;
                if (block.blockType === ContentBlockType.PDF) {
                  card = <DocumentCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                } else if (block.blockType === ContentBlockType.REDIRECT) {
                  card = <RedirectCard key={block.id} block={block} />;
                } else if (block.blockType === ContentBlockType.EMBED) {
                  card = <EmbedCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                } else if (block.blockType === ContentBlockType.FORM) {
                  card = <FormCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                } else if (block.blockType === ContentBlockType.GCS_UPLOAD) {
                  card = <GcsUploadCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                } else if (block.blockType === ContentBlockType.EVENT) {
                  card = <ZoomCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                } else {
                  card = <TextCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                }
                return withDraftBadge(block, card);
              })}
            </Box>
          </Box>
          <Divider sx={{ my: 4, ...rowWidthSx }} />
        </>
      )}

      {!hasSubLessons && quizBlocks.length > 0 && (
        <Box sx={{ pb: 4, ...(isInlineLayout && { width: '100%', maxWidth: { md: INLINE_ROW_MAX } }) }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
          >
            {quizSectionHeading}
          </Typography>
          {quizBlocks.map(block =>
            withDraftBadge(
              block,
              <QuizCard
                key={block.id}
                title={block.title || 'Quiz'}
                quizId={block.quizId || ''}
                onClick={() => onNavigateToBlock(block)}
              />,
            ),
          )}
        </Box>
      )}

      {/* Promo banner — footer. Sticks to the bottom of the viewport on mobile,
          renders inline at the end of the page on desktop. */}
      <PromoBannerSlot
        entityId={lesson.id}
        position={PromoBannerPosition.FOOTER}
        sx={{
          ...rowWidthSx,
          position: { xs: 'sticky', md: 'static' },
          bottom: { xs: 16, md: 'auto' },
          zIndex: { xs: theme.zIndex.appBar, md: 'auto' },
        }}
      />
    </Box>
  );
}
